import { FC, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { ToggleButtonGroup } from '@mui/material'
import Modal, { TModelProps } from '../../../../shared/modal/Modal'
import ModalTitle from '../../../../shared/modal/ModalTitle'
import ModalContent from '../../../../shared/modal/ModalContent'
import ModalActions from '../../../../shared/modal/ModalActions'
import Button from '../../../../shared/Button'
import Checkbox from '../../../../shared/Checkbox'
import ToggleButton from '../../../../shared/ToggleButton'
import { useFilterContext } from '../../state/useFilter'
import { useFiltersStateContext } from '../../state/useFiltersState'
import { trackFilter } from '../../utils'

const CounterStyled = styled.span`
  color: ${({ theme }) => theme.colors.grey.dark};
  font-size: 18px;
  font-weight: 400;
`

const SectionStyled = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.grey.light};
  border: ${({ theme }) => `1px solid ${theme.colors.grey.primary}`};
  border-radius: 4px;

  & + & {
    margin-top: 12px;
  }
`

const SectionTitleStyled = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.black};
`

const ToggleRowStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`

const ToggleButtonGroupStyled = styled(ToggleButtonGroup)`
  background-color: ${({ theme }) => theme.colors.white};
`

const ToggleLabelStyled = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #373b3e;
`

const CheckboxContainerStyled = styled.div`
  display: grid;
  grid-row-gap: 8px;
  overflow-x: hidden;
`

type TDataAccessModalProps = TModelProps

const DataAccessFilterModal: FC<TDataAccessModalProps> = ({ closeModal, open, ...rest }) => {
  const { filterData: { name, subFilters = [] } } = useFilterContext()
  const { setFilterValues, getFilterValues } = useFiltersStateContext()

  const deliveryMethod = subFilters.find((filter) => filter.shortName === 'delivery_method')
  const accessBurden = subFilters.find((filter) => filter.shortName === 'access_burden')
  const eligibility = subFilters.find((filter) => filter.shortName === 'eligibility')
  const accessBurdenLevels = accessBurden?.value ?? []

  const [deliveryValues, setDeliveryValues] = useState<string[]>([])
  const [accessBurdenValues, setAccessBurdenValues] = useState<string[]>([])
  const [eligibilityValues, setEligibilityValues] = useState<string[]>([])
  const [includeLowerLevels, setIncludeLowerLevels] = useState(true)

  useLayoutEffect(() => {
    if (open) {
      setDeliveryValues((getFilterValues('delivery_method') as any)?.filters ?? [])
      setEligibilityValues((getFilterValues('eligibility') as any)?.filters ?? [])
      const accessBurdenState = getFilterValues('access_burden') as any
      setAccessBurdenValues(accessBurdenState?.filters ?? [])
      setIncludeLowerLevels(accessBurdenState?.includeLowerLevels ?? true)
    }
  }, [open])

  const closeModalHandler = () => {
    closeModal?.()
  }

  const totalSelected = deliveryValues.length + accessBurdenValues.length + eligibilityValues.length

  const toggleDelivery = (value: string) => {
    setDeliveryValues((current) => (
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    ))
  }

  const toggleEligibility = (value: string) => {
    setEligibilityValues((current) => (
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    ))
  }

  const toggleAccessBurden = (value: string) => {
    if (!includeLowerLevels) {
      setAccessBurdenValues((current) => (current.includes(value) ? [] : [value]))
      return
    }

    const isSelected = accessBurdenValues.includes(value)
    const selectedIndex = accessBurdenLevels.indexOf(value)

    // Keep the checked set a contiguous prefix from the lowest level: checking a level
    // cascades checks down to it, unchecking a level cascades unchecks up from it too,
    // so a higher level can never stay checked while a lower one it depends on isn't.
    setAccessBurdenValues(
      isSelected ? accessBurdenLevels.slice(0, selectedIndex) : accessBurdenLevels.slice(0, selectedIndex + 1)
    )
  }

  const handleIncludeLowerLevelsChange = (_event: unknown, value: 'yes' | 'no' | null) => {
    if (!value) return
    setIncludeLowerLevels(value === 'yes')
    setAccessBurdenValues([])
  }

  const isAccessBurdenDisabled = (value: string) => (
    !includeLowerLevels && accessBurdenValues.length > 0 && !accessBurdenValues.includes(value)
  )

  const handleApply = () => {
    if (deliveryMethod) {
      setFilterValues('delivery_method', deliveryValues)
      trackFilter(deliveryMethod.name, deliveryValues)
    }
    if (accessBurden) {
      setFilterValues('access_burden', accessBurdenValues, undefined, includeLowerLevels)
      trackFilter(accessBurden.name, accessBurdenValues)
    }
    if (eligibility) {
      setFilterValues('eligibility', eligibilityValues)
      trackFilter(eligibility.name, eligibilityValues)
    }
    closeModalHandler()
  }

  return (
    <Modal {...rest} open={open} closeModal={closeModalHandler} withCloseButton>
      <ModalTitle sx={{ paddingBottom: '16px' }}>
        {name}
        <CounterStyled>
          {' '}
          (
          {totalSelected}
          {' '}
          selected)
        </CounterStyled>
      </ModalTitle>
      <ModalContent sx={{ paddingBottom: '8px' }}>
        {deliveryMethod && (
          <SectionStyled>
            <SectionTitleStyled>{deliveryMethod.name}</SectionTitleStyled>
            <CheckboxContainerStyled>
              {(deliveryMethod.value ?? []).map((variant) => (
                <Checkbox
                  checked={deliveryValues.includes(variant)}
                  onChange={() => toggleDelivery(variant)}
                  key={variant}
                  label={variant}
                  sx={{ width: 'fit-content' }}
                />
              ))}
            </CheckboxContainerStyled>
          </SectionStyled>
        )}

        {accessBurden && (
          <SectionStyled>
            <SectionTitleStyled>{accessBurden.name}</SectionTitleStyled>
            <ToggleRowStyled>
              <ToggleLabelStyled>Include lower burden levels in addition to the selected level</ToggleLabelStyled>
              <ToggleButtonGroupStyled
                exclusive
                value={includeLowerLevels ? 'yes' : 'no'}
                onChange={handleIncludeLowerLevelsChange}
              >
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroupStyled>
            </ToggleRowStyled>
            <CheckboxContainerStyled>
              {accessBurdenLevels.map((variant) => (
                <Checkbox
                  checked={accessBurdenValues.includes(variant)}
                  onChange={() => toggleAccessBurden(variant)}
                  disabled={isAccessBurdenDisabled(variant)}
                  key={variant}
                  label={variant}
                  sx={{ width: 'fit-content' }}
                />
              ))}
            </CheckboxContainerStyled>
          </SectionStyled>
        )}

        {eligibility && (
          <SectionStyled>
            <SectionTitleStyled>{eligibility.name}</SectionTitleStyled>
            <CheckboxContainerStyled>
              {(eligibility.value ?? []).map((variant) => (
                <Checkbox
                  checked={eligibilityValues.includes(variant)}
                  onChange={() => toggleEligibility(variant)}
                  key={variant}
                  label={variant}
                  sx={{ width: 'fit-content' }}
                />
              ))}
            </CheckboxContainerStyled>
          </SectionStyled>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={closeModalHandler}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApply}>
          Apply
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default DataAccessFilterModal
